import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  useProjectIntegrations,
  useUpdateIntegration,
  useVerifyIntegration,
} from '../hooks/useProjectIntegrations';
import { useSettings } from '../hooks/useSettings';
import api from '../lib/axios';
import Modal from './ui/Modal';
import {
  relativeTime,
  GscConnectPanel,
  Ga4ConnectPanel,
  CroTrackerConnectPanel,
} from './integrations/ConnectPanels';

// Offizielle Marken-Icons als inline SVG (kein externer Request, keine
// Abhängigkeit von einer Icon-Library). Jedes SVG ist so gebaut, dass es sich
// auf den weißen Container gut absetzt.
function IntegrationLogo({ type, logoUrl }) {
  const box = 'w-full h-full flex items-center justify-center';

  if (type === 'cro_tracker') {
    if (logoUrl) {
      return (
        <div className={box}>
          <img
            src={logoUrl}
            alt="OptimusAI"
            className="w-6 h-6 object-contain"
            style={{ filter: 'brightness(0)' }}
          />
        </div>
      );
    }
    return <div className={`${box} text-white text-[15px] font-bold`}>OAI</div>;
  }

  if (type === 'gsc') {
    // Google "G" (vier Farben), vereinfacht
    return (
      <svg viewBox="0 0 48 48" className="w-6 h-6">
        <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
        <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.6 16 18.9 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
        <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3c-2 1.5-4.6 2.5-7.3 2.5-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
        <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4 5.5l6.3 5.3C41.2 35.6 44 30.3 44 24c0-1.3-.1-2.6-.4-3.9z"/>
      </svg>
    );
  }

  if (type === 'ga4') {
    // Google Analytics Icon (orange Balken + gelber Kreis)
    return (
      <svg viewBox="0 0 192 192" className="w-6 h-6">
        <path fill="#F9AB00" d="M130 29v132c0 14.77 10.2 23 21 23 10 0 21-7 21-23V30c0-13.54-10-22-21-22s-21 9.33-21 21z"/>
        <path fill="#E37400" d="M75 96v65c0 14.77 10.2 23 21 23 10 0 21-7 21-23V97c0-13.54-10-22-21-22s-21 9.33-21 21z"/>
        <circle fill="#E37400" cx="41" cy="163" r="21"/>
      </svg>
    );
  }

  if (type === 'cockpit') {
    // Kunden Cockpit — Gauge-/Cockpit-Icon
    return (
      <svg viewBox="0 0 48 48" className="w-6 h-6" fill="none">
        <path d="M24 8C15.2 8 8 15.2 8 24h4a12 12 0 0 1 24 0h4c0-8.8-7.2-16-16-16z" fill="#0F766E"/>
        <circle cx="24" cy="24" r="3" fill="#0F766E"/>
        <path d="M24 24l8-6" stroke="#0F766E" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    );
  }

  if (type === 'google_business') {
    // Google Business — Pin-/Map-Marker-Icon in Google-Farben
    return (
      <svg viewBox="0 0 48 48" className="w-6 h-6">
        <path
          fill="#1A73E8"
          d="M24 4c-7.2 0-13 5.8-13 13 0 9.75 13 27 13 27s13-17.25 13-27c0-7.2-5.8-13-13-13z"
        />
        <circle cx="24" cy="17" r="5" fill="#FFFFFF" />
      </svg>
    );
  }

  if (type === 'sea') {
    // Google Ads Icon (blau-gelb-grün stilisiert)
    return (
      <svg viewBox="0 0 192 192" className="w-6 h-6">
        <path fill="#FBBC04" d="M77.8 18.6L11.6 133.3c-7.8 13.5-3.1 30.9 10.4 38.7s30.9 3.1 38.7-10.4L127 46.9c7.8-13.5 3.1-30.9-10.4-38.7s-30.9-3.1-38.8 10.4z"/>
        <path fill="#4285F4" d="M114.4 18.6L180.6 133.3c7.8 13.5 3.1 30.9-10.4 38.7s-30.9 3.1-38.7-10.4L65.3 46.9c-7.8-13.5-3.1-30.9 10.4-38.7s30.9-3.1 38.7 10.4z"/>
        <circle fill="#34A853" cx="36.2" cy="156.8" r="28.3"/>
      </svg>
    );
  }

  if (type === 'cms') {
    // Headless CMS — stilisiertes Dokument-Icon
    return (
      <svg viewBox="0 0 48 48" className="w-6 h-6" fill="none">
        <rect x="11" y="8" width="26" height="32" rx="3" fill="#fff" />
        <path d="M16 16h16M16 22h16M16 28h10" stroke="#0D3B50" strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }

  return <div className={`${box} text-white text-[16px]`}>◆</div>;
}

const ICON_MAP = {
  activity: '◉',
  search: '◎',
  'bar-chart': '▯',
  target: '⊙',
};

// Modul-Farben je Integrationstyp (Container-Hintergrund)
const TYPE_COLORS = {
  cro_tracker: '#0C9488',      // Teal — Brand
  gsc: '#1C2E3C',              // Navy — SEO
  ga4: '#C2410C',              // Burnt Orange — Analytics
  sea: '#6D28D9',              // Purple — SEA
  cockpit: '#0F766E',          // Deep Teal — Kunden Cockpit
  google_business: '#1A73E8',  // Google Blue — Business Profile
  cms: '#0D3B50',              // Navy — Headless CMS
};
const DEFAULT_TYPE_COLOR = '#1C2E3C';

const STATUS_STYLES = {
  connected: { bg: '#D1FAE5', color: '#047857', label: 'Verbunden' },
  pending: { bg: '#FEF3C7', color: '#B45309', label: 'Ausstehend' },
  error: { bg: '#FEE2E2', color: '#B91C1C', label: 'Fehler' },
  inactive: { bg: '#F3F4F6', color: '#6B7280', label: 'Nicht aktiv' },
};

// Pro Integrationstyp können Status-Labels überschrieben werden.
const STATUS_LABEL_OVERRIDES = {
  cro_tracker: {
    connected: 'OAI aktiv',
    pending: 'OAI ausstehend',
    error: 'OAI Fehler',
    inactive: 'OAI Tracking nicht aktiv',
  },
};

function StatusBadge({ status, type }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.inactive;
  const label = STATUS_LABEL_OVERRIDES[type]?.[status] || s.label;
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full"
      style={{ backgroundColor: s.bg, color: s.color }}
    >
      <span className="w-[5px] h-[5px] rounded-full" style={{ backgroundColor: s.color }} />
      {label}
    </span>
  );
}

function IntegrationCard({ integration, onVerify, onToggleBooked, onSetup, verifying, logoUrl }) {
  const navigate = useNavigate();
  const isSoon = integration.setup === 'soon';
  const faded = isSoon && !integration.is_booked;
  const color = TYPE_COLORS[integration.type] || DEFAULT_TYPE_COLOR;
  const isCroTracker = integration.type === 'cro_tracker';

  return (
    <div
      className={`rounded-xl shadow-sm p-4 flex flex-col gap-3 text-white transition-opacity ${
        faded ? 'opacity-60' : ''
      }`}
      style={{ backgroundColor: color }}
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg shrink-0 bg-white p-1.5">
          <IntegrationLogo type={integration.type} logoUrl={logoUrl} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-semibold text-[13.5px] text-white truncate">{integration.label}</div>
            {isSoon && (
              <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-white/20 text-white/90">
                DEMNÄCHST
              </span>
            )}
          </div>
          <div className="text-[11px] text-white/60 truncate">{integration.short}</div>
        </div>
        <StatusBadge status={integration.status} type={integration.type} />
      </div>

      <p className="text-[11.5px] text-white/80 leading-snug line-clamp-2">
        {integration.description}
      </p>

      {integration.last_error && (
        <div className="text-[11px] text-white bg-red-500/30 rounded-md px-2.5 py-1.5 leading-snug">
          {integration.last_error}
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-white/70">
        <span>
          {integration.last_verified_at
            ? `Geprüft ${relativeTime(integration.last_verified_at)}`
            : 'Noch nicht geprüft'}
        </span>
        <label className="flex items-center gap-1.5 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={integration.is_booked}
            onChange={(e) => onToggleBooked(e.target.checked)}
            className="accent-white"
          />
          <span>Gebucht</span>
        </label>
      </div>

      <div className="flex items-center gap-2 pt-2 border-t border-white/15">
        {isSoon ? (
          <button
            disabled
            className="flex-1 text-[12px] font-medium text-white/60 py-1.5 rounded-md bg-white/10 cursor-not-allowed"
          >
            Bald verfügbar
          </button>
        ) : isCroTracker ? (
          <>
            <button
              onClick={onSetup}
              className="flex-1 text-[12px] font-medium text-white py-1.5 rounded-md bg-white/15 hover:bg-white/25"
              title="Tracking-Snippet einrichten (optional)"
            >
              OptimusAI Tracking
            </button>
            <button
              onClick={() => navigate('/cro/analyses?new=1')}
              className="flex-1 text-[12px] font-semibold text-white py-1.5 rounded-md bg-white/25 hover:bg-white/35"
              title="Neue CRO-Analyse für dieses Projekt starten"
            >
              Zur Analyse →
            </button>
          </>
        ) : (
          <>
            <button
              onClick={onSetup}
              className="flex-1 text-[12px] font-medium text-white py-1.5 rounded-md bg-white/15 hover:bg-white/25"
            >
              {integration.status === 'connected' ? 'Details' : 'Einrichten'}
            </button>
            {integration.supports_verify && (
              <button
                onClick={onVerify}
                disabled={verifying}
                className="text-[12px] font-medium text-white px-3 py-1.5 rounded-md hover:bg-white/15 disabled:opacity-50"
              >
                {verifying ? 'Prüfe…' : 'Prüfen'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function CockpitConnectPanel({ integration, projectId }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [label, setLabel] = useState(integration.config?.label || '');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(null);

  const isActive = !!integration.is_booked;

  const enable = async (on) => {
    setSaving(true);
    setSavedFlash(null);
    try {
      await api.patch(
        `/projects/${projectId}/integrations/cockpit`,
        {
          is_booked: on,
          config: { label: label.trim() },
        },
        { skipProject: true }
      );
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      setSavedFlash({
        type: 'ok',
        text: on
          ? 'Kunden Cockpit aktiviert — in der Sidebar sichtbar.'
          : 'Kunden Cockpit deaktiviert.',
      });
      setTimeout(() => setSavedFlash(null), 3500);
    } catch (e) {
      setSavedFlash({ type: 'err', text: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[12.5px] text-gray-600 leading-relaxed">
        Das Kunden Cockpit ist ein natives, projektgebundenes Performance-Dashboard. Es zeigt
        alle aktiven Module in einer Ansicht — CRO-Analysen, SEO-Rankings &amp; Maßnahmen,
        Monatsreports sowie (sobald verbunden) GA4-Traffic und SEA-Kampagnen. Später teilbar
        per Share-Link.
      </p>

      <div>
        <label className="text-[12px] font-medium text-gray-700 block mb-1.5">
          Titel für den Kunden (optional)
        </label>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="z. B. Performance Cockpit"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal"
        />
        <div className="text-[10.5px] text-gray-400 mt-1">
          Standard: „Kunden Cockpit". Wird als Überschrift im Dashboard angezeigt.
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => enable(!isActive)}
          disabled={saving}
          className={`flex-1 rounded-lg py-2 text-[12.5px] font-medium disabled:opacity-50 ${
            isActive
              ? 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
              : 'bg-teal text-white hover:opacity-90'
          }`}
        >
          {saving
            ? 'Speichere…'
            : isActive
              ? 'Cockpit deaktivieren'
              : 'Cockpit aktivieren'}
        </button>
        {isActive && (
          <button
            onClick={() => navigate('/cockpit')}
            className="text-[12.5px] px-4 py-2 bg-teal text-white rounded-lg font-medium hover:opacity-90"
          >
            Öffnen →
          </button>
        )}
      </div>

      {savedFlash && (
        <div
          className={`text-[11.5px] rounded-md px-3 py-2 ${
            savedFlash.type === 'ok' ? 'bg-teal-light text-teal' : 'bg-red-50 text-red-700'
          }`}
        >
          {savedFlash.type === 'ok' ? '✓ ' : '⚠ '}
          {savedFlash.text}
        </div>
      )}
    </div>
  );
}

function CmsConnectPanel({ integration, projectId }) {
  const qc = useQueryClient();
  const [baseUrl, setBaseUrl] = useState(integration.config?.base_url || '');
  const [token, setToken] = useState('');
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);

  const hasToken = !!integration.config?.has_token;
  const connected = integration.status === 'connected';

  const saveAndVerify = async () => {
    setSaving(true);
    setFlash(null);
    try {
      const config = { base_url: baseUrl.trim() };
      if (token.trim()) config.token = token.trim();
      await api.patch(`/projects/${projectId}/integrations/cms`, { config }, { skipProject: true });
      const { data } = await api.post(`/projects/${projectId}/integrations/cms/verify`, {}, { skipProject: true });
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      const it = data?.integration;
      if (it?.status === 'connected') {
        setFlash({ type: 'ok', text: `Verbunden ✓ — ${it.config?.pages_seen ?? 0} Seiten sichtbar.` });
        setToken('');
      } else {
        setFlash({ type: 'err', text: it?.last_error || 'Verbindung fehlgeschlagen.' });
      }
    } catch (e) {
      setFlash({ type: 'err', text: e.response?.data?.message || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-[12.5px] text-gray-600 leading-relaxed">
        Verbinde die Headless-CMS-Instanz dieses Kunden. Danach kann der Über-Agent Maßnahmen per
        „Umsetzen" direkt im CMS schreiben (Title, Meta …). Den API-Token erzeugst du in der
        CMS-Instanz mit{' '}
        <code className="px-1 bg-gray-100 rounded text-[11px]">php token.php --name='Plattform Agent'</code>.
      </p>

      <div>
        <label className="text-[12px] font-medium text-gray-700 block mb-1.5">CMS-Basis-URL</label>
        <input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://cms.kunde.de"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal"
        />
      </div>

      <div>
        <label className="text-[12px] font-medium text-gray-700 block mb-1.5">
          API-Token {hasToken && <span className="text-teal font-normal">· hinterlegt</span>}
        </label>
        <input
          type="password"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          placeholder={hasToken ? '•••••••• (neu eingeben zum Ersetzen)' : 'cms_…'}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-[12.5px] outline-none focus:border-teal font-mono"
        />
      </div>

      <button
        onClick={saveAndVerify}
        disabled={saving || !baseUrl.trim() || (!token.trim() && !hasToken)}
        className="w-full rounded-lg py-2 text-[12.5px] font-medium bg-teal text-white hover:opacity-90 disabled:opacity-50"
      >
        {saving ? 'Verbinde…' : connected ? 'Erneut prüfen' : 'Verbinden & prüfen'}
      </button>

      {flash && (
        <div
          className={`text-[11.5px] rounded-md px-3 py-2 ${
            flash.type === 'ok' ? 'bg-teal-light text-teal' : 'bg-red-50 text-red-700'
          }`}
        >
          {flash.type === 'ok' ? '✓ ' : '⚠ '}
          {flash.text}
        </div>
      )}
    </div>
  );
}

function SetupModal({ integration, projectId, onClose }) {
  if (!integration) return null;

  return (
    <Modal onClose={onClose}>
      <h2 className="text-[16px] font-bold mb-1">{integration.label}</h2>
      <p className="text-[12px] text-gray-500 mb-4">{integration.description}</p>

      {integration.type === 'cro_tracker' && (
        <CroTrackerConnectPanel projectId={projectId} />
      )}

      {integration.type === 'gsc' && (
        <GscConnectPanel integration={integration} projectId={projectId} />
      )}

      {integration.type === 'ga4' && (
        <Ga4ConnectPanel integration={integration} projectId={projectId} />
      )}

      {integration.type === 'cockpit' && (
        <CockpitConnectPanel integration={integration} projectId={projectId} />
      )}

      {integration.type === 'cms' && (
        <CmsConnectPanel integration={integration} projectId={projectId} />
      )}

      <div className="flex justify-end mt-5">
        <button onClick={onClose} className="px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50 rounded-lg">
          Schließen
        </button>
      </div>
    </Modal>
  );
}

export default function IntegrationGrid({ projectId }) {
  const { data, isLoading } = useProjectIntegrations(projectId);
  const { data: appSettings } = useSettings();
  const updateMut = useUpdateIntegration(projectId);
  const verifyMut = useVerifyIntegration(projectId);
  const [setupFor, setSetupFor] = useState(null);
  const [verifyingType, setVerifyingType] = useState(null);
  const logoUrl = appSettings?.logo_url || '';

  if (!projectId) return null;

  const items = data?.integrations || [];
  const readiness = data?.readiness || { active: 0, total: items.length };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-semibold text-[14.5px]">Integrationen</h3>
          <p className="text-[11.5px] text-gray-500">Tracking, SEO & weitere Datenquellen für dieses Projekt</p>
        </div>
        <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-teal-light text-teal">
          {readiness.active} von {readiness.total} aktiv
        </span>
      </div>

      {isLoading ? (
        <div className="py-8 text-center text-[12px] text-gray-400">Lade Integrationen…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <IntegrationCard
              key={it.type}
              integration={it}
              logoUrl={logoUrl}
              verifying={verifyingType === it.type && verifyMut.isPending}
              onVerify={() => {
                setVerifyingType(it.type);
                verifyMut.mutate(it.type, { onSettled: () => setVerifyingType(null) });
              }}
              onToggleBooked={(v) => updateMut.mutate({ type: it.type, is_booked: v })}
              onSetup={() => setSetupFor(it)}
            />
          ))}
        </div>
      )}

      {setupFor && (
        <SetupModal
          integration={items.find((i) => i.type === setupFor.type) || setupFor}
          projectId={projectId}
          onClose={() => setSetupFor(null)}
        />
      )}
    </div>
  );
}
